/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import React, {
    ForwardedRef,
    forwardRef,
    PropsWithChildren,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';
import ProcessStatus from '../../../models/processStatusModel';
import User from '../../../models/userModel';
import Account from '../../../models/accountModel';
import {getAdminAccounts, getAllProcessStatusByType} from '../../../util/requestSender';
import {ResponseData} from '@dvrd/dvr-controls';
import {ActivateHandle, ProcessType, UserFilter} from '../../../util/interfaces';
import AdminProcess from './adminProcess';
import UsersFilterController from '../users/filter/usersFilterController';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    processType: ProcessType;
    onSelectAccount?: (user: User | null, account: Account | null) => void;
}

export type AdminProcessRef = {
    getSelectedUser: () => User | null;
    getSelectedAccount: () => Account | null;
    onLoadProcesses: VoidFunction;
};

const LIMIT = 10;

function AdminProcessController(props: PropsWithChildren<Props>, ref: ForwardedRef<AdminProcessRef>) {
    const {processType, children} = props;
    const {t} = useTranslation();
    const [processes, setProcesses] = useState<Array<ProcessStatus>>([]);
    const [loadingProcesses, setLoadingProcesses] = useState(false);
    const [users, setUsers] = useState<Array<User>>([]);
    const [accounts, setAccounts] = useState<Array<Account>>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [page, setPage] = useState(0);
    const [search, setSearch] = useState('');
    const filterRef = useRef<ActivateHandle>(null);
    const processTimeoutRef = useRef<number | null>(null);

    function onChangeSearch(value: string) {
        setSearch(value.trim());
    }

    function onChangePage(nextPage: number) {
        return function () {
            setPage(nextPage);
            loadUsers({page: nextPage});
        };
    }

    function onEnterSearch() {
        setPage(0);
        loadUsers({page: 0});
    }

    function onOpenFilter() {
        filterRef.current?.activate();
    }

    function onSubmitFilter(filter: UserFilter) {
        setPage(0);
        loadUsers({filter, page: 0});
    }

    function onSelectUser(user: User) {
        return function () {
            setSelectedUser(user);
            onSelectAccount(null)();
        };
    }

    function onSelectAccount(account: Account | null) {
        return function () {
            setSelectedAccount(account);
            props.onSelectAccount?.(selectedUser, account);
        };
    }

    function onDeleteProcess(process: ProcessStatus) {
        return function () {
            setLoadingProcesses(true);
            process.delete((process: ProcessStatus, success: boolean, data: ResponseData) => {
                setLoadingProcesses(false);
                if (success) {
                    setProcesses(processes.filter((proc: ProcessStatus) => proc.id !== process.id));
                    showSnackbar(t('admin.adminProcess.deletedSnack', 'Het proces is verwijderd'));
                } else
                    showDialog(
                        data.message ??
                            t('admin.adminProcess.deleteFailedMsg', 'Het verwijderen van het proces is niet gelukt.')
                    );
            });
        };
    }

    function getSelectedUser(): User | null {
        return selectedUser;
    }

    function getSelectedAccount(): Account | null {
        return selectedAccount;
    }

    function loadAccounts() {
        setAccounts([]);
        if (!selectedUser) return;
        setLoadingAccounts(true);
        getAdminAccounts({
            id: selectedUser.id,
            callback: (data: ResponseData) => {
                setLoadingAccounts(false);
                if (data.success) setAccounts(data.accounts.map((acc: any) => new Account(acc)));
                else
                    showDialog(
                        data.message ??
                            t('admin.adminProcess.loadAccountsFailedMsg', 'Het laden van de accounts is niet gelukt.'),
                        t('admin.adminProcess.loadAccountsFailedTitle', 'Account niet gevonden')
                    );
            }
        });
    }

    function loadUsers(params?: {search?: string; page?: number; filter?: UserFilter}) {
        setLoadingUsers(true);
        const _page = params?.page ?? page;
        const _search = params?.search ?? search;
        const _filter = params?.filter;
        User.getAdminUsers(LIMIT, _page, _search, _filter, (users: User[], success: boolean, data: ResponseData) => {
            setLoadingUsers(false);
            if (data.success) {
                setUsers(users);
                setSelectedUser(null);
            } else
                showDialog(
                    data.message ??
                        t('admin.adminProcess.loadUsersFailedMsg', 'Het laden van de gebruikers is niet gelukt.'),
                    t('admin.adminProcess.loadUsersFailedTitle', 'Laden mislukt')
                );
        });
    }

    function loadProcessStatuses() {
        clearTimeout();
        getAllProcessStatusByType({
            process_type: processType,
            callback: (data: ResponseData) => {
                if (data.success) setProcesses(data.process_statuses.map((proc: any) => new ProcessStatus(proc)));
                processTimeoutRef.current = window.setTimeout(loadProcessStatuses, 3000);
            }
        });
    }

    function clearTimeout() {
        if (processTimeoutRef.current !== null) {
            window.clearTimeout(processTimeoutRef.current);
            processTimeoutRef.current = null;
        }
    }

    useEffect(() => {
        loadAccounts();
        return function () {
            clearTimeout();
        };
    }, [selectedUser]);

    useEffect(() => {
        loadUsers();
        loadProcessStatuses();
        return function () {
            clearTimeout();
        };
    }, []);

    useImperativeHandle(ref, () => ({
        getSelectedUser,
        getSelectedAccount,
        onLoadProcesses: loadProcessStatuses
    }));

    return (
        <>
            <AdminProcess
                onDeleteProcess={onDeleteProcess}
                onEnterSearch={onEnterSearch}
                onOpenFilter={onOpenFilter}
                onSelectUser={onSelectUser}
                onSelectAccount={onSelectAccount}
                onChangePage={onChangePage}
                onChangeSearch={onChangeSearch}
                search={search}
                processes={processes}
                limit={LIMIT}
                loadingAccounts={loadingAccounts}
                loadingProcesses={loadingProcesses}
                page={page}
                accounts={accounts}
                selectedUser={selectedUser}
                selectedAccount={selectedAccount}
                users={users}
                loadingUsers={loadingUsers}
            >
                {children}
            </AdminProcess>
            <UsersFilterController ref={filterRef} onSubmit={onSubmitFilter} />
        </>
    );
}

export default forwardRef<AdminProcessRef, PropsWithChildren<Props>>(AdminProcessController);
